const DISPLAY_NAMES: Record<string, string> = {
  factset_ai_query: "FactSet AI",
  finster_query: "Finster",
  filings_query: "Filings",
  id_lookup: "FactSet company lookup",
  set_citations: "Set citations",
  list_sheets: "Inspect workbook",
  get_snapshot: "Snapshot",
  find_calc_errors: "Check calculation errors",
  find_circular_references: "Check circular references",
  read_range: "Read range",
  modify_workbook: "Edit workbook",
  set_range_values: "Write values",
  set_range_formulas: "Write formulas",
  format_range: "Format range",
  create_sheet: "Create sheet",
  rename_sheet: "Rename sheet",
  activate_sheet: "Activate sheet",
  generate_formula: "Generate formula",
  retrieve_template: "Find template",
  apply_template: "Apply template",
  refresh_factset_data: "Refresh FactSet data",
  recalculate_workbook: "Recalculate workbook",
  chart_creator: "FactSet ActiveGraph",
  read_charts: "Read charts",
  // Named for the built-in modeling catalog specifically, so it cannot be
  // confused with the user-authored Skills banner above the timeline.
  get_skill: "Load modeling guidance",
};

const INTERNAL_TOOLS = new Set<string>();

const RESEARCH_TOOLS = new Set([
  "factset_ai_query",
  "finster_query",
  "filings_query",
]);

/**
 * Returns the user-facing label for an executor tool, falling back to a
 * title-cased version of unknown tool ids.
 */
export function getExcelAgentToolDisplayName(tool: string): string {
  const known = DISPLAY_NAMES[tool];
  if (known) return known;
  return tool
    .replace(/^get_/, "")
    .replace(/_data$/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Checks whether a tool is an internal control tool that should be hidden from
 * the user-facing timeline.
 */
export function isExcelInternalTool(tool: string): boolean {
  return INTERNAL_TOOLS.has(tool);
}

/**
 * Checks whether a tool is a research call that can expose nested reasoning and
 * retrieved sources.
 */
export function isExcelResearchTool(tool: string): boolean {
  return RESEARCH_TOOLS.has(tool);
}

/**
 * Builds a compact, human-readable preview of tool arguments for timeline pills.
 */
export function summarizeExcelToolArgs(tool: string, args: unknown): string | undefined {
  if (!args || typeof args !== "object") return undefined;
  const record = args as Record<string, unknown>;
  switch (tool) {
    case "get_skill":
      return summarizeSkillIds(record.skillIds);
    case "factset_ai_query":
    case "finster_query":
    case "filings_query":
    case "retrieve_template":
      return firstString(record.query, record.prompt, record.description);
    case "generate_formula":
      return summarizeFormulaQueries(record);
    case "id_lookup":
      return summarizeIdLookups(record);
    case "ask_user":
      return firstString(record.question);
    case "set_citations": {
      const citations = Array.isArray(record.citations) ? record.citations : [];
      return citations.length > 0
        ? `${citations.length} citation${citations.length === 1 ? "" : "s"}`
        : undefined;
    }
    case "read_range":
    case "get_snapshot":
      return formatSheetRange(record.sheetName, record.address ?? record.range);
    case "set_range_values":
    case "set_range_formulas":
    case "format_range":
      return formatSheetRange(record.sheetName, record.address ?? record.range);
    case "modify_workbook":
      return summarizeWorkbookModification(record.modifications ?? record);
    case "create_sheet":
      return firstString(record.sheetName, record.name, "New worksheet");
    case "rename_sheet": {
      const from = firstString(record.sheetName, record.oldName, record.name);
      const to = firstString(record.newName);
      return from && to ? `${from} to ${to}` : from ?? to;
    }
    case "activate_sheet":
      return firstString(record.sheetName, record.name);
    case "refresh_factset_data":
      return summarizeSheets(record.sheetNames) ??
        (record.scope === "workbook" || record.refreshAll === true
          ? "All sheets"
          : undefined);
    case "recalculate_workbook":
      return firstString(record.mode);
    case "chart_creator":
      return summarizeChartCreator(record);
    case "read_charts":
      return summarizeReadCharts(record);
    case "apply_template":
      return summarizeTemplate(record);
    default:
      return firstString(record.query, record.address, record.range, record.sheetName, record.targetSheet, record.reason);
  }
}

/**
 * Summarize both the internal single-company input and the model-facing batch.
 *
 * @param record - Parsed `id_lookup` arguments.
 * @returns A compact company list suitable for the tool pill.
 */
function summarizeIdLookups(record: Record<string, unknown>): string | undefined {
  const legacySearchText = firstString(record.search_text);
  if (legacySearchText) return legacySearchText;
  if (!Array.isArray(record.lookups)) return undefined;

  const companies = record.lookups.flatMap((lookup) => {
    if (!lookup || typeof lookup !== "object" || Array.isArray(lookup)) return [];
    const searchText = firstString(
      (lookup as Record<string, unknown>).search_text,
    );
    return searchText ? [searchText] : [];
  });
  if (companies.length === 0) {
    return record.lookups.length > 0
      ? `${record.lookups.length} ${record.lookups.length === 1 ? "company" : "companies"}`
      : undefined;
  }

  const visible = companies.slice(0, 2).join(", ");
  const remaining = companies.length - 2;
  return remaining > 0 ? `${visible} +${remaining}` : visible;
}

/**
 * Summarizes `get_skill` results using requested skill names where available.
 */
export function summarizeExcelSkillResult(result: unknown): string | undefined {
  if (!result || typeof result !== "object") return undefined;
  const skills = (result as { skills?: unknown }).skills;
  if (!Array.isArray(skills)) return undefined;
  const requested = skills
    .filter((item): item is Record<string, unknown> => Boolean(item && typeof item === "object"))
    .filter((item) => item.requested !== false);
  const source = requested.length > 0 ? requested : skills;
  const labels = source
    .map((item) => {
      const record = item as Record<string, unknown>;
      return firstString(record.name) ?? (typeof record.skillId === "string" ? SKILL_LABELS[record.skillId] ?? titleizeSkillId(record.skillId) : undefined);
    })
    .filter((item): item is string => Boolean(item));
  if (labels.length === 0) return undefined;
  return labels.length <= 3 ? labels.join(", ") : `${labels.slice(0, 3).join(", ")} +${labels.length - 3} more`;
}

const SKILL_LABELS: Record<string, string> = {
  dcf: "DCF",
  lbo: "LBO",
  trading_comps: "Trading Comps",
  precedent_transactions: "Precedent Transactions",
  operating_model: "Operating Model",
  three_statement: "Three Statement",
  debt_schedule: "Debt Schedule",
  returns_analysis: "Returns Analysis",
  sensitivity_scenario: "Sensitivity Scenario",
  ev_bridge: "EV Bridge",
  formatting_style: "Formatting",
  structure_layout: "Structure & Layout",
  data_formula: "Data & Formula",
  qa_validation: "QA Validation",
};

/**
 * Summarizes one or more requested skill ids for display.
 */
function summarizeSkillIds(value: unknown): string | undefined {
  const ids = Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string" && item.trim().length > 0)
    : typeof value === "string" && value.trim()
      ? [value.trim()]
      : [];
  if (ids.length === 0) return undefined;
  const labels = ids.map((id) => SKILL_LABELS[id] ?? titleizeSkillId(id));
  return labels.length <= 3 ? labels.join(", ") : `${labels.slice(0, 3).join(", ")} +${labels.length - 3} more`;
}

/**
 * Converts a skill id such as `trading_comps` into title case.
 */
function titleizeSkillId(id: string): string {
  return id
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Returns the first non-empty string in a list of unknown values.
 */
function firstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return undefined;
}

/**
 * Preserves the model's complete formula request for the pill tooltip. Native
 * runs normally use `queries`; legacy and single-metric callers use `query`.
 */
function summarizeFormulaQueries(record: Record<string, unknown>): string | undefined {
  const queries = Array.isArray(record.queries)
    ? record.queries
        .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
        .map((value) => value.trim())
    : [];
  return queries.length > 0
    ? queries.join("\n")
    : firstString(record.query, record.prompt, record.description);
}

/**
 * Formats a sheet/range pair for compact display.
 */
function formatSheetRange(sheet: unknown, range: unknown): string | undefined {
  const sheetText = firstString(sheet);
  const rangeText = firstString(range);
  if (sheetText && rangeText) return `${sheetText}!${rangeText}`;
  return rangeText ?? sheetText;
}

/**
 * Summarizes a sheet-name array, capping long lists.
 */
function summarizeSheets(value: unknown): string | undefined {
  if (!Array.isArray(value)) return undefined;
  const sheets = value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
  if (sheets.length === 0) return undefined;
  return sheets.length <= 3 ? sheets.join(", ") : `${sheets.slice(0, 3).join(", ")} +${sheets.length - 3} more`;
}

/**
 * Summarizes a workbook modification payload by affected sheet when possible.
 */
function summarizeWorkbookModification(value: unknown): string | undefined {
  if (!value || typeof value !== "object") return undefined;
  const record = value as Record<string, unknown>;
  const worksheets = Array.isArray(record.worksheets) ? record.worksheets : [];
  const sheets = Array.isArray(record.sheets) ? record.sheets : [];
  const worksheetNames = worksheets
    .map((item) => (item && typeof item === "object" ? firstString((item as Record<string, unknown>).sheetName) : undefined))
    .filter((item): item is string => Boolean(item));
  if (worksheetNames.length > 0) return summarizeSheets(worksheetNames);
  if (sheets.length > 0) return `${sheets.length} sheet ${sheets.length === 1 ? "change" : "changes"}`;
  return firstString(record.message) ?? "Workbook edits";
}

/**
 * Summarizes chart creator arguments by ticker and chart type.
 */
function summarizeChartCreator(record: Record<string, unknown>): string | undefined {
  const params = record.chartParameters && typeof record.chartParameters === "object"
    ? record.chartParameters as Record<string, unknown>
    : {};
  const tickers = Array.isArray(params.tickers)
    ? params.tickers.filter((item): item is string => typeof item === "string" && item.trim().length > 0)
    : [];
  const chartType = firstString(params.chartType);
  if (tickers.length > 0 && chartType) return `${tickers.join(", ")} ${chartType}`;
  return tickers.length > 0 ? tickers.join(", ") : chartType ?? firstString(record.operation);
}

/**
 * Summarizes read-charts arguments by target sheet and chart index.
 */
function summarizeReadCharts(record: Record<string, unknown>): string | undefined {
  const sheetRef = record.sheetRef && typeof record.sheetRef === "object"
    ? record.sheetRef as Record<string, unknown>
    : {};
  const sheet = firstString(sheetRef.name);
  const chartIndex = typeof record.chartIndex === "number" ? `chart ${record.chartIndex}` : undefined;
  return [sheet, chartIndex].filter(Boolean).join(" ") || undefined;
}

/**
 * Summarizes template application arguments by template and identifier.
 */
function summarizeTemplate(record: Record<string, unknown>): string | undefined {
  const template = firstString(record.templateName, record.templateKey, record.key);
  const inputs = record.inputs && typeof record.inputs === "object"
    ? record.inputs as Record<string, unknown>
    : {};
  const identifier = firstString(inputs.main_identifier, inputs.identifier, record.identifier);
  if (template && identifier) return `${template} for ${identifier}`;
  return template ?? identifier;
}
